import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  BarChart3, 
  FileText, 
  User, 
  BookOpen, 
  Users, 
  ArrowRightLeft, 
  Eye,
  CheckCircle,
  Star,
  Shield,
  Zap,
  Brain,
  MessageCircle,
  TrendingUp,
  Award,
  ArrowRight,
  Menu,
  X,
  Clock,
  Lock,
  Smartphone,
  Headphones,
  Target,
  Heart,
  Activity
} from 'lucide-react';
import SabiwabeeLogo from '../components/SabiwabeeLogo';

const Landing = () => {
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [isFeatureModalOpen, setIsFeatureModalOpen] = useState(false);

  const features = [
    {
      icon: Brain,
      title: "AI-Powered Therapy",
      description: "Connect with intelligent AI therapists for personalized mental health support and guidance.",
      color: "text-blue-600",
      detailedDescription: "Experience 24/7 access to AI therapists trained in cognitive behavioral therapy, mindfulness, and crisis intervention. Our AI provides personalized responses based on your mood, history, and specific needs. Features include real-time conversation, mood tracking integration, and automatic escalation to human therapists when needed.",
      benefits: ["24/7 availability", "Personalized responses", "Crisis detection", "Seamless human handover"],
      iconDetail: "Advanced AI trained on therapeutic techniques"
    },
    {
      icon: BarChart3,
      title: "Progress Tracking",
      description: "Monitor your mental health journey with detailed analytics and mood tracking.",
      color: "text-green-600",
      detailedDescription: "Track your mental health progress with comprehensive analytics including mood trends, therapy session insights, and goal achievement. Visualize your journey with interactive charts and receive personalized insights about patterns and improvements. Set and monitor mental health goals with actionable recommendations.",
      benefits: ["Mood trend analysis", "Goal tracking", "Personalized insights", "Progress visualization"],
      iconDetail: "Data-driven insights for better mental health outcomes"
    },
    {
      icon: FileText,
      title: "Mental Health Assessments",
      description: "Take comprehensive assessments to understand your mental health status and get personalized recommendations.",
      color: "text-purple-600",
      detailedDescription: "Access scientifically validated mental health assessments covering anxiety, depression, stress, and more. Receive detailed reports with personalized recommendations and track changes over time. Assessments are designed by mental health professionals and provide actionable insights for your wellness journey.",
      benefits: ["Scientifically validated", "Personalized reports", "Progress tracking", "Professional insights"],
      iconDetail: "Evidence-based assessments for accurate mental health evaluation"
    },
    {
      icon: BookOpen,
      title: "Digital Journal",
      description: "Express your thoughts and feelings in a private, secure digital journal with AI insights.",
      color: "text-orange-600",
      detailedDescription: "Write freely in your private, encrypted digital journal with AI-powered insights that help you understand patterns in your thoughts and emotions. Features include mood tagging, gratitude prompts, and automatic sentiment analysis. Your journal entries are completely private and secure.",
      benefits: ["Private & secure", "AI insights", "Mood tagging", "Gratitude prompts"],
      iconDetail: "AI-enhanced journaling for deeper self-reflection"
    },
    {
      icon: User,
      title: "Meditation & Mindfulness",
      description: "Access guided meditation sessions and mindfulness exercises for stress relief.",
      color: "text-indigo-600",
      detailedDescription: "Access a comprehensive library of guided meditations, breathing exercises, and mindfulness practices tailored to your needs. From quick 5-minute stress relief sessions to longer deep meditation practices, find what works best for you. Track your meditation progress and build a consistent mindfulness practice.",
      benefits: ["Guided sessions", "Breathing exercises", "Progress tracking", "Personalized recommendations"],
      iconDetail: "Guided mindfulness practices for stress relief and mental clarity"
    },
    {
      icon: Users,
      title: "Peer Support Community",
      description: "Connect with others on similar journeys in our supportive community forums.",
      color: "text-pink-600",
      detailedDescription: "Join a supportive community of individuals on similar mental health journeys. Share experiences, find encouragement, and connect with others who understand. Our moderated forums ensure a safe, supportive environment where you can find community and reduce feelings of isolation.",
      benefits: ["Safe environment", "Shared experiences", "Community support", "Moderated forums"],
      iconDetail: "Supportive community to reduce isolation and build connections"
    },
    {
      icon: ArrowRightLeft,
      title: "Therapist Handover",
      description: "Seamlessly transition between AI support and human therapists when needed.",
      color: "text-teal-600",
      detailedDescription: "When you need human support, seamlessly transition from AI to licensed therapists. Our system maintains context and history, ensuring your human therapist has all the information they need. Available for crisis situations, complex issues, or when you prefer human interaction.",
      benefits: ["Seamless transition", "Context preservation", "Licensed therapists", "Crisis support"],
      iconDetail: "Smooth transition from AI to human mental health professionals"
    },
    {
      icon: Eye,
      title: "AI Transparency",
      description: "Understand how AI makes decisions with our transparent and ethical AI framework.",
      color: "text-cyan-600",
      detailedDescription: "We believe in complete transparency about how our AI works. Understand the reasoning behind AI recommendations, access detailed explanations of therapeutic approaches, and learn about our ethical AI framework. We're committed to building trust through openness and explainability.",
      benefits: ["Transparent reasoning", "Ethical framework", "Explainable AI", "Trust building"],
      iconDetail: "Transparent and ethical AI for trustworthy mental health support"
    }
  ];

  const handleFeatureClick = (feature) => {
    setSelectedFeature(feature);
    setIsFeatureModalOpen(true);
  };

  const closeFeatureModal = () => {
    setIsFeatureModalOpen(false);
    setSelectedFeature(null);
  };

  const benefits = [
    "24/7 AI therapy support",
    "Personalized mental health assessments",
    "Secure and private",
    "Progress tracking and analytics",
    "Community support forums",
    "Meditation and mindfulness tools",
    "Ethical AI framework",
    "Professional therapist integration"
  ];

  const testimonials = [
    {
      name: "Sarah M.",
      role: "Student",
      content: "Sabiwabee has been a game-changer for my anxiety. The AI therapist is always there when I need support.",
      rating: 5
    },
    {
      name: "Michael R.",
      role: "Professional",
      content: "The progress tracking helps me understand my mental health patterns. Highly recommend!",
      rating: 5
    },
    {
      name: "Emma L.",
      role: "Parent",
      content: "The community support is amazing. I've found so much comfort connecting with others.",
      rating: 5
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center space-x-2">
              <div className="flex items-center justify-center w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl shadow-md transform hover:scale-105 transition-transform duration-300">
                <SabiwabeeLogo className="w-4 h-4 text-white" />
              </div>
              <span className="text-xl font-bold text-gray-900">
                Sabiwabee
              </span>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <a href="#features" className="text-gray-600 hover:text-blue-600 transition-colors">Features</a>
              <Link to="/about" className="text-gray-600 hover:text-blue-600 transition-colors">About</Link>
              <a href="#testimonials" className="text-gray-600 hover:text-blue-600 transition-colors">Testimonials</a>
              <Link 
                to="/login" 
                className="text-blue-600 hover:text-blue-700 font-medium transition-colors"
              >
                Sign In
              </Link>
              <Link 
                to="/register" 
                className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-2 rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-200 shadow-md hover:shadow-lg"
              >
                Get Started
              </Link>
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 text-gray-600 hover:text-gray-900"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200">
            <div className="px-4 py-2 space-y-1">
              <a href="#features" className="block px-3 py-2 text-gray-600 hover:text-blue-600">Features</a>
              <Link to="/about" className="block px-3 py-2 text-gray-600 hover:text-blue-600">About</Link>
              <a href="#testimonials" className="block px-3 py-2 text-gray-600 hover:text-blue-600">Testimonials</a>
              <Link to="/login" className="block px-3 py-2 text-blue-600 font-medium">Sign In</Link>
              <Link to="/register" className="block px-3 py-2 bg-blue-600 text-white rounded-lg text-center">Get Started</Link>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              Your AI-Powered
              <span className="block bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Mental Health Companion
              </span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Experience personalized mental health support with our intelligent AI therapists, 
              comprehensive assessments, and supportive community. Your journey to better mental health starts here.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/register" 
                className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center"
              >
                Start Your Journey
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
              <Link 
                to="/login" 
                className="border-2 border-blue-600 text-blue-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-600 hover:text-white transition-all duration-200 flex items-center justify-center"
              >
                Sign In
              </Link>
            </div>
          </div>
        </div>

        {/* Floating Elements */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-blue-200 rounded-full opacity-20 animate-pulse"></div>
        <div className="absolute top-40 right-20 w-16 h-16 bg-purple-200 rounded-full opacity-20 animate-pulse delay-1000"></div>
        <div className="absolute bottom-20 left-20 w-12 h-12 bg-pink-200 rounded-full opacity-20 animate-pulse delay-2000"></div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Everything You Need for Better Mental Health
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Our comprehensive platform combines AI technology with human-centered design 
              to provide you with the tools and support you need.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div 
                  key={index} 
                  className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-xl border border-gray-200 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 cursor-pointer group"
                  onClick={() => handleFeatureClick(feature)}
                >
                  <div className={`w-12 h-12 bg-gradient-to-r from-blue-100 to-purple-100 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-200`}>
                    <Icon className={`w-6 h-6 ${feature.color}`} />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                  <div className="mt-4 flex items-center text-blue-600 text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    Learn more
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gradient-to-r from-blue-50 to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Why Choose Sabiwabee?
              </h2>
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
              <div className="mt-8">
                <Link 
                  to="/register" 
                  className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-200 shadow-md hover:shadow-lg inline-flex items-center"
                >
                  Sign Up
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </div>
            </div>
            <div className="relative">
              <div className="bg-white p-8 rounded-2xl shadow-xl">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center">
                    <Brain className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">AI-Powered Insights</h3>
                    <p className="text-gray-600">Personalized recommendations based on your unique needs</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Mood Tracking</span>
                    <div className="flex space-x-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star key={star} className="w-4 h-4 text-yellow-400 fill-current" />
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Privacy & Security</span>
                    <Shield className="w-5 h-5 text-green-500" />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">24/7 Availability</span>
                    <Zap className="w-5 h-5 text-blue-500" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              What Our Users Say
            </h2>
            <p className="text-xl text-gray-600">
              Join thousands of users who have transformed their mental health journey
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-xl border border-gray-200">
                <div className="flex items-center space-x-1 mb-4">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star key={star} className="w-4 h-4 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">"{testimonial.content}"</p>
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center">
                    <span className="text-white font-semibold">{testimonial.name.charAt(0)}</span>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{testimonial.name}</p>
                    <p className="text-gray-600 text-sm">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Start Your Mental Health Journey?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Join thousands of users who have already transformed their mental health 
            with Sabiwabee's AI-powered platform.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/register" 
              className="bg-white text-blue-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center"
            >
              Get Started Free
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
            <Link 
              to="/login" 
              className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-blue-600 transition-all duration-200 flex items-center justify-center"
            >
              Sign In
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <div className="flex items-center justify-center w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl shadow-md transform hover:scale-105 transition-transform duration-300">
                  <SabiwabeeLogo className="w-4 h-4 text-white" />
                </div>
                <span className="text-xl font-bold text-white">Sabiwabee</span>
              </div>
              <p className="text-gray-400">
                Your AI-powered mental health companion for a better tomorrow.
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Features</h3>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#features" className="hover:text-white transition-colors">AI-Powered Therapy</a></li>
                <li><a href="#features" className="hover:text-white transition-colors">Progress Tracking</a></li>
                <li><a href="#features" className="hover:text-white transition-colors">Mental Health Assessments</a></li>
                <li><a href="#features" className="hover:text-white transition-colors">Digital Journal</a></li>
                <li><a href="#features" className="hover:text-white transition-colors">Meditation & Mindfulness</a></li>
                <li><a href="#features" className="hover:text-white transition-colors">Peer Support Community</a></li>
                <li><a href="#features" className="hover:text-white transition-colors">Therapist Handover</a></li>
                <li><a href="#features" className="hover:text-white transition-colors">AI Transparency</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Support</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/help-center" className="hover:text-white transition-colors">Help Center</Link></li>
                <li><Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms-of-service" className="hover:text-white transition-colors">Terms of Service</Link></li>
                <li><Link to="/help-center" className="hover:text-white transition-colors">Contact Us</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Get Started</h3>
              <div className="space-y-2">
                <Link 
                  to="/register" 
                  className="block bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-lg text-center hover:from-blue-700 hover:to-purple-700 transition-all duration-200"
                >
                  Sign Up Free
                </Link>
                <Link 
                  to="/login" 
                  className="block border border-gray-600 text-white px-4 py-2 rounded-lg text-center hover:bg-gray-800 transition-all duration-200"
                >
                  Sign In
                </Link>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 Sabiwabee. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Feature Detail Modal */}
      {isFeatureModalOpen && selectedFeature && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-8">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-4">
                  <div className={`w-16 h-16 bg-gradient-to-r from-blue-100 to-purple-100 rounded-xl flex items-center justify-center`}>
                    <selectedFeature.icon className={`w-8 h-8 ${selectedFeature.color}`} />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">{selectedFeature.title}</h2>
                    <p className="text-gray-600">{selectedFeature.iconDetail}</p>
                  </div>
                </div>
                <button
                  onClick={closeFeatureModal}
                  className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Content */}
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Overview</h3>
                  <p className="text-gray-700 leading-relaxed">{selectedFeature.detailedDescription}</p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Key Benefits</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {selectedFeature.benefits.map((benefit, index) => (
                      <div key={index} className="flex items-center space-x-3">
                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                        <span className="text-gray-700">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-6 rounded-xl">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Ready to try {selectedFeature.title}?</h3>
                  <p className="text-gray-700 mb-4">Start your mental health journey today and experience the benefits of {selectedFeature.title.toLowerCase()}.</p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Link 
                      to="/register" 
                      className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-200 text-center"
                      onClick={closeFeatureModal}
                    >
                      Get Started Free
                    </Link>
                    <button
                      onClick={closeFeatureModal}
                      className="border-2 border-blue-600 text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-blue-600 hover:text-white transition-all duration-200"
                    >
                      Learn More Features
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Landing; 